import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { Block } from '../api/client.ts';
import { blockWindow, placeSegments } from '../lib/timeline.ts';

interface Props {
  block: Block;
  height?: number;
}

/**
 * The block's waveform in miniature (spec §10.2).
 *
 * A coverage bar says how much of the half hour exists; this says whether
 * anyone is talking in it. Both matter when scanning two dozen rows for the one
 * you want, which the brief asks to be possible in under three seconds.
 *
 * Drawn from the thumbnail carried on the block listing, so a grid of 24 rows
 * costs no extra requests. A segment without one yet shows as a faint line
 * rather than a hole, since the audio is there either way.
 *
 * Left to right, like every other time axis here.
 */
export function BlockThumbnail({ block, height = 26 }: Props): React.ReactElement {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const measure = (): void => {
      const measured = Math.floor(wrap.getBoundingClientRect().width);
      setWidth((current) => (measured > 0 && measured !== current ? measured : current));
    };
    measure();
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measure);
      observer.observe(wrap);
    }
    globalThis.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      globalThis.removeEventListener('resize', measure);
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || width === 0) return;

    const dpr = globalThis.devicePixelRatio || 1;
    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const style = getComputedStyle(canvas);
    const read = (name: string, fallback: string): string =>
      style.getPropertyValue(name).trim() || fallback;
    const wave = read('--thumb-wave', 'rgba(246,239,226,0.5)');
    const pending = read('--wave-pending', 'rgba(246,239,226,0.16)');

    const win = blockWindow(block);
    const span = win.toMs - win.fromMs;
    if (span <= 0) return;
    const mid = height / 2;

    for (const placed of placeSegments(block)) {
      const from = Math.max(0, ((placed.fromMs - win.fromMs) / span) * width);
      const to = Math.min(width, ((placed.toMs - win.fromMs) / span) * width);
      if (to <= from) continue;

      const data = placed.segment.thumbnail;
      if (!data || data.length === 0) {
        ctx.fillStyle = pending;
        ctx.fillRect(from, mid - 1, to - from, 2);
        continue;
      }

      const pairs = data.length / 2;
      ctx.fillStyle = wave;
      for (let x = Math.floor(from); x < to; x += 1) {
        const index = Math.min(pairs - 1, Math.floor(((x - from) / (to - from)) * pairs));
        const min = data[index * 2] ?? 0;
        const max = data[index * 2 + 1] ?? 0;
        // Thumbnails are 8-bit like the peaks they came from.
        const top = mid - (max / 128) * mid;
        const bottom = mid - (min / 128) * mid;
        ctx.fillRect(x, top, 1, Math.max(1, bottom - top));
      }
    }
  }, [block, width, height]);

  return (
    <div className="thumbnail" ref={wrapRef}>
      <canvas ref={canvasRef} style={{ width: '100%', height }} aria-hidden="true" />
    </div>
  );
}
